import type { DrawStroke, Reaction, RoomSettings } from '@kinop/shared';
import { DrawCanvas, type BrushSettings } from './DrawCanvas.js';
import { DrawToolbar } from './DrawToolbar.js';
import { ReactionLayer } from './ReactionLayer.js';

/** Быстрые реакции из панели под кадром. */
export const QUICK_EMOJIS = ['😂', '😱', '🔥', '😭', '🤡', '👏'];

export interface FrameToolsState {
  brush: BrushSettings;
  drawMode: boolean;
}

export const DEFAULT_BRUSH: BrushSettings = {
  tool: 'brush',
  color: '#9184d9',
  size: 0.008,
  eraser: false,
  neon: true,
  stamp: '❤️',
};

interface OverlayProps {
  reactions: Reaction[];
  strokes: DrawStroke[];
  settings: RoomSettings;
  serverNow: () => number;
  drawMode: boolean;
  canDraw: boolean;
  brush: BrushSettings;
  onStroke: (stroke: Omit<DrawStroke, 'id' | 'authorClientId' | 'createdAt'>) => void;
}

/**
 * Слой поверх кадра: реакции и рисунок.
 *
 * Живёт внутри сцены плеера, поэтому одинаково работает и со своим файлом,
 * и со встроенным плеером площадки — тот лежит в iframe, но наш холст
 * находится над ним и события мыши перехватывает первым.
 */
export function FrameOverlay({
  reactions,
  strokes,
  settings,
  serverNow,
  drawMode,
  canDraw,
  brush,
  onStroke,
}: OverlayProps): JSX.Element {
  return (
    <>
      <DrawCanvas
        strokes={strokes}
        trailSeconds={settings.trailSeconds}
        serverNow={serverNow}
        active={drawMode && canDraw}
        brush={brush}
        onStroke={onStroke}
      />
      <ReactionLayer reactions={reactions} serverNow={serverNow} />
    </>
  );
}

interface ToolbarProps {
  settings: RoomSettings;
  canDraw: boolean;
  drawMode: boolean;
  brush: BrushSettings;
  onToggleDraw: () => void;
  onBrushChange: (patch: Partial<BrushSettings>) => void;
  onReaction: (emoji: string) => void;
  onUndo: () => void;
  onClear: () => void;
}

/** Панель под кадром: быстрые реакции и рисование. */
export function FrameToolbar({
  settings,
  canDraw,
  drawMode,
  brush,
  onToggleDraw,
  onBrushChange,
  onReaction,
  onUndo,
  onClear,
}: ToolbarProps): JSX.Element {
  return (
    <div className="frame-tools">
      <div className="frame-tools__row">
        <div className="emoji-bar">
          {(settings.reactions === false ? [] : QUICK_EMOJIS).map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="emoji-btn"
              aria-label={`Отправить реакцию ${emoji}`}
              onClick={() => onReaction(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>

        <div className="frame-tools__spacer" />

        {canDraw ? (
          <>
            <button
              type="button"
              className="chip chip--solid"
              data-on={drawMode ? '' : undefined}
              aria-pressed={drawMode}
              onClick={onToggleDraw}
            >
              ✎ Рисовать
            </button>
            <button type="button" className="chip" onClick={onClear}>
              Стереть
            </button>
          </>
        ) : (
          <span className="frame-tools__note">Рисование выключено ведущим</span>
        )}
      </div>

      {drawMode && canDraw && (
        <>
          <DrawToolbar brush={brush} onChange={onBrushChange} onUndo={onUndo} />
          <p className="frame-tools__note">
            Линии растворяются через {settings.trailSeconds}&nbsp;с. Нажатие по кадру без режима
            рисования отправляет реакцию.
          </p>
        </>
      )}
    </div>
  );
}
